/**
 * The theme builder, as the themes see it (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 6 — Custom, as the console has it"). Pure — no DOM, no storage.
 *
 * The builder (components/theme-switcher.tsx) keeps its settings where it always has — localStorage's
 * theme-color, theme-custom-hex, theme-type and sidebar-variant — and applies them exactly as before.
 * What changed is where they show: under Custom alone, and for someone who never chose a theme (whose
 * page must look exactly as it did). This file answers what the themes need to know about them.
 */
import { THEME_COLORS } from '@/lib/constants/themes'
import { APP_CUSTOM_THEME, APP_THEME_META, type AppThemeChoice } from './app-themes'
import { BG_DEFAULT, isPageToneId, isSidebarToneId } from './app-backgrounds'

/** The builder's settings, as it holds them (its localStorage values). */
export interface BuilderSettings {
  /** A grid colour's name, or `custom` for the person's own colour in `hex`. */
  color: string
  /** The person's own colour: six hex digits (or whatever is half-typed in the field). */
  hex: string
  /** `default`, `scaled` or `mono`. */
  style: string
  /** `default` (docked), `floating` or `inset`. */
  sidebar: string
  /** The page's background tone: `default` (no override) or an id of PAGE_TONES (app-backgrounds.ts). */
  pageBg: string
  /** The sidebar's background tone: `default` (no override) or an id of SIDEBAR_TONES. */
  sidebarBg: string
}

/** What someone who never opened the builder has. */
export const DEFAULT_BUILDER: Readonly<BuilderSettings> = Object.freeze({
  color: 'default',
  hex: '',
  style: 'default',
  sidebar: 'default',
  pageBg: BG_DEFAULT,
  sidebarBg: BG_DEFAULT,
})

const SIX_HEX = /^[0-9a-fA-F]{6}$/
const GRID = new Map<string, string>(THEME_COLORS.map((c) => [c.name, c.value]))
const DEFAULT_COLOUR = GRID.get('default') as string

/** The colour the builder paints: a grid colour's value, the own colour as `#hex`, or Classic's own. */
export function builderColourValue(settings: BuilderSettings): string {
  if (settings.color === 'custom') return SIX_HEX.test(settings.hex) ? `#${settings.hex.toLowerCase()}` : DEFAULT_COLOUR
  return GRID.get(settings.color) ?? DEFAULT_COLOUR
}

/**
 * Does the builder paint anything Classic does not? A grid colour other than Default, an own colour of
 * six hex digits, a style other than the default, a sidebar other than docked, or a page or sidebar
 * background tone the catalogue holds. A value the builder has no rule for paints nothing, so it does
 * not count.
 */
export function builderIsCustomised(settings: BuilderSettings): boolean {
  const colour =
    settings.color === 'custom' ? SIX_HEX.test(settings.hex) : settings.color !== 'default' && GRID.has(settings.color)
  const style = settings.style === 'scaled' || settings.style === 'mono'
  const sidebar = settings.sidebar === 'floating' || settings.sidebar === 'inset'
  const pageBg = isPageToneId(settings.pageBg)
  const sidebarBg = isSidebarToneId(settings.sidebarBg)
  return colour || style || sidebar || pageBg || sidebarBg
}

/**
 * The theme the menu shows as chosen. The saved choice, if there is one. Someone who never chose sees
 * exactly the look they had — the builder's — so the menu calls it Custom when the builder is
 * customised, and Classic when it is not (the two look the same then). Nothing is saved for them
 * until they pick a card or touch the builder.
 */
export function effectiveAppTheme(saved: AppThemeChoice | null, settings: BuilderSettings): AppThemeChoice {
  if (saved !== null) return saved
  return builderIsCustomised(settings) ? APP_CUSTOM_THEME : 'classic'
}

export type AppSidebarVariant = 'sidebar' | 'floating' | 'inset'

/**
 * The app sidebar's variant for the theme on screen (`shown`, a preview included): the builder's under
 * Custom and for someone who never chose; docked on Classic; a ready-made theme's own otherwise —
 * Harbor floats, as it does in the console.
 */
export function appSidebarVariant(shown: AppThemeChoice | null, builderVariant: AppSidebarVariant): AppSidebarVariant {
  if (shown === null || shown === APP_CUSTOM_THEME) return builderVariant
  return APP_THEME_META[shown].sidebar
}
